import {
  BookmarkIcon,
  ChevronLeftIcon,
  HamburgerMenuIcon,
  OpenInNewWindowIcon,
  PersonIcon,
  PlusCircledIcon,
} from "@radix-ui/react-icons";
import {
  Box,
  DropdownMenu,
  Flex,
  Heading,
  IconButton,
  Link,
  Separator,
} from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAppLogo from "../../assets/use-app-icon.png";
import { authClient } from "../../authentication/core/client/auth-client";
import { getErrorMessage } from "../../authentication/core/client/auth-client-errors";
import { env } from "../../env";
import { routes } from "../../routes";
import { isNativeIOSPlatform } from "../../shared/utils/environment";
import { IconLogout, IconMap } from "./icons";

type HeaderWithMenuProps = {
  title: string;
  homePath: string;
  menuItems: { name: string; path: string }[];
  primaryAction?: React.ReactNode;
};

type HeaderWhenEmbeddedProps = {
  title: string;
};

/** Reduced, non-interactive header for pages hosted inside another app. */
export const HeaderWhenEmbedded: React.FC<HeaderWhenEmbeddedProps> = ({
  title,
}) => (
  <Box asChild width="100%">
    <header>
      <Flex direction="row" gap="2" align="center">
        <img src={useAppLogo} className="shared-logo-small" alt="Use logo" />
        <Heading as="h1" truncate>
          {title}
        </Heading>
      </Flex>
      <Box mt="2">
        <Separator size="4" />
      </Box>
    </header>
  </Box>
);

export const HeaderWithMenu: React.FC<HeaderWithMenuProps> = ({
  homePath,
  title,
  menuItems,
  primaryAction,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNativeIOS = isNativeIOSPlatform();
  const isLoggedIn = authClient.useIsLoggedIn();
  const signOutMutation = useMutation({
    mutationFn: async () => {
      await authClient.signOut();
    },
    onSuccess: () => {
      void navigate(routes.auth.login, { replace: true });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  return (
    <Box width="100%">
      <Flex direction="row" gap="2" align="center">
        {isNativeIOS && (
          <IconButton
            color="gray"
            variant="ghost"
            onClick={() => {
              void navigate(-1);
            }}
            style={{ flexShrink: "0" }}
          >
            <ChevronLeftIcon width="18" height="18" />
          </IconButton>
        )}
        <RouterLink to={homePath} style={{ flexShrink: "0" }}>
          <img src={useAppLogo} className="shared-logo-small" alt="Use logo" />
        </RouterLink>

        <Box flexGrow="1" minWidth="0">
          <RouterLink to={homePath}>
            <Heading as="h1" truncate>
              {title}
            </Heading>
          </RouterLink>
        </Box>

        {primaryAction}

        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <IconButton color="gray" variant="outline">
              <HamburgerMenuIcon width="18" height="18" />
            </IconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            {/* We had to use custom styles for the menu items because using 'asChild' produced errors when opening the menu (#BRA-41). */}
            {menuItems.map((menuItem) => (
              <DropdownMenu.Item
                key={menuItem.path}
                className="dropdown-menu-item"
              >
                <RouterLink
                  to={menuItem.path}
                  className="dropdown-menu-item-content"
                >
                  <PlusCircledIcon />
                  <span>{menuItem.name}</span>
                </RouterLink>
              </DropdownMenu.Item>
            ))}

            <DropdownMenu.Item className="dropdown-menu-item">
              <RouterLink
                to={routes.bookings.my}
                className="dropdown-menu-item-content"
              >
                <BookmarkIcon />
                <span>Meine Buchungen</span>
              </RouterLink>
            </DropdownMenu.Item>

            <DropdownMenu.Item className="dropdown-menu-item">
              <RouterLink
                to={routes.home}
                className="dropdown-menu-item-content"
              >
                <IconMap width={15} height={15} />
                <span>Karte</span>
              </RouterLink>
            </DropdownMenu.Item>

            {isLoggedIn ? (
              <>
                <DropdownMenu.Item className="dropdown-menu-item">
                  <RouterLink
                    to={routes.account}
                    className="dropdown-menu-item-content"
                  >
                    <PersonIcon />
                    <span>Mein Profil</span>
                  </RouterLink>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className="dropdown-menu-item"
                  disabled={signOutMutation.isPending}
                  onSelect={() => {
                    signOutMutation.mutate();
                  }}
                >
                  <span className="dropdown-menu-item-content">
                    <IconLogout width={15} height={15} fill="currentColor" />
                    <span>Ausloggen</span>
                  </span>
                </DropdownMenu.Item>
              </>
            ) : (
              <DropdownMenu.Item className="dropdown-menu-item">
                <RouterLink
                  to={`${routes.auth.login}?next=${encodeURIComponent(location.pathname + location.search)}`}
                  className="dropdown-menu-item-content"
                >
                  <PersonIcon />
                  <span>Anmelden</span>
                </RouterLink>
              </DropdownMenu.Item>
            )}

            <DropdownMenu.Item className="dropdown-menu-item">
              <Link
                href={env.contactPage}
                target="_blank"
                className="dropdown-menu-item-content"
              >
                <OpenInNewWindowIcon />
                <span>Kontakt</span>
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className="dropdown-menu-item">
              <Link
                href="https://www.usegroup.eu/impressum"
                target="_blank"
                className="dropdown-menu-item-content"
              >
                <OpenInNewWindowIcon />
                <span>Impressum</span>
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item className="dropdown-menu-item">
              <Link
                href="https://www.usegroup.eu/datenschutz"
                target="_blank"
                className="dropdown-menu-item-content"
              >
                <OpenInNewWindowIcon />
                <span>Datenschutz</span>
              </Link>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Flex>

      <Box mt="2">
        <Separator size="4" />
      </Box>
    </Box>
  );
};
